import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getRankInfo } from '../utils/rankUtils';
import { supabase } from '../lib/supabase';
import SkeletonLoader from './SkeletonLoader';
import './Leaderboard.css';

interface LeaderboardProps {
  onNavigate: (page: string) => void;
}

interface LeaderboardEntry {
  rank: number;
  name: string;
  rollNo: string;
  points: number;
  department: string;
  id: string;
}

interface DepartmentStats {
  department: string;
  totalPoints: number;
  memberCount: number;
  averagePoints: number;
  rank: number;
}

const Leaderboard: React.FC<LeaderboardProps> = ({ onNavigate }) => {
  const { user } = useAuth();
  const [leaderboardData, setLeaderboardData] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserRank, setCurrentUserRank] = useState<number>(0);
  const [currentUserData, setCurrentUserData] = useState<LeaderboardEntry | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [activeTab, setActiveTab] = useState<'all' | 'department' | 'departments'>('all');
  const [departmentStats, setDepartmentStats] = useState<DepartmentStats[]>([]);
  const itemsPerPage = 50;

  useEffect(() => {
    if (activeTab === 'departments') {
      fetchDepartmentStats();
    } else {
      fetchLeaderboard(0);
      fetchUserRank();
    }
    fetchTotalUsers();
  }, [activeTab]);

  const fetchTotalUsers = async () => {
    try {
      const { count } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });
      
      setTotalUsers(count || 0);
    } catch (error) {
    }
  };

  const fetchLeaderboard = async (pageNum: number, append = false) => {
    try {
      if (!append) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      // Fetch users from profiles table with pagination
      let query = supabase
        .from('profiles')
        .select('id, name, roll_no, points, department')
        .order('points', { ascending: false });
      
      // Filter by department if "Your Department" tab is active
      if (activeTab === 'department' && user?.department) {
        query = query.eq('department', user.department);
      }
      
      const { data: profiles, error } = await query
        .range(pageNum * itemsPerPage, (pageNum + 1) * itemsPerPage - 1);

      if (error) throw error;

      // Transform data and add ranks
      const rankedData: LeaderboardEntry[] = profiles?.map((profile, index) => ({
        rank: (pageNum * itemsPerPage) + index + 1,
        name: profile.name || 'Anonymous',
        rollNo: profile.roll_no || 'Unknown',
        points: profile.points || 0,
        department: profile.department || 'Unknown',
        id: profile.id
      })) || [];

      if (append) {
        setLeaderboardData(prev => [...prev, ...rankedData]);
      } else {
        setLeaderboardData(rankedData);
      }

      // Check if there are more items to load
      setHasMore(profiles?.length === itemsPerPage);
    } catch (error) {
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const fetchUserRank = async () => {
    if (!user) return;
    
    try {
      // Get user's exact rank
      const { count } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gt('points', user.points || 0);
      
      const rank = (count || 0) + 1;
      setCurrentUserRank(rank);

      // Fetch user's data
      const { data: userData } = await supabase
        .from('profiles')
        .select('id, name, roll_no, points, department')
        .eq('id', user.id)
        .single();

      if (userData) {
        setCurrentUserData({
          rank,
          name: userData.name || 'Anonymous',
          rollNo: userData.roll_no || 'Unknown',
          points: userData.points || 0,
          department: userData.department || 'Unknown',
          id: userData.id
        });
      }
    } catch (error) {
    }
  };

  const loadMore = () => {
    if (!loadingMore && hasMore) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchLeaderboard(nextPage, true);
    }
  };


  // Get rank info for each user
  const getRankBadge = (points: number) => {
    const rank = getRankInfo(getRankFromPoints(points));
    return rank.icon;
  };
  
  const fetchDepartmentStats = async () => {
    try {
      setLoading(true);
      
      // Fetch all profiles to calculate department statistics
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('department, points');
      
      if (error) throw error;
      
      // Group by department and calculate stats
      const deptMap: Record<string, { points: number; count: number }> = {};
      
      profiles?.forEach(profile => {
        const dept = profile.department || 'Unknown';
        if (!deptMap[dept]) {
          deptMap[dept] = { points: 0, count: 0 };
        }
        deptMap[dept].points += profile.points || 0;
        deptMap[dept].count += 1;
      });
      
      // Convert to array and calculate averages
      const stats: DepartmentStats[] = Object.entries(deptMap)
        .map(([department, data]) => ({
          department,
          totalPoints: data.points,
          memberCount: data.count,
          averagePoints: Math.round(data.points / data.count),
          rank: 0
        }))
        .sort((a, b) => b.totalPoints - a.totalPoints)
        .map((dept, index) => ({ ...dept, rank: index + 1 }));
      
      setDepartmentStats(stats);
    } catch (error) {
      console.error('Error fetching department stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankFromPoints = (points: number) => {
    if (points >= 2000) return 'legendary';
    if (points >= 1000) return 'campus-hero';
    if (points >= 600) return 'green-champion';
    if (points >= 300) return 'eco-warrior';
    if (points >= 100) return 'cleaner';
    return 'beginner';
  };

  return (
    <div className="leaderboard-page">
      <div className="leaderboard-header">
        <button onClick={() => onNavigate('dashboard')} className="back-button">
          ←
        </button>
        <h1>🏆 Leaderboard</h1>
        <p>Top Campus Contributors</p>
        {totalUsers > 0 && (
          <p className="total-users">{totalUsers.toLocaleString()} total participants</p>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="leaderboard-tabs">
        <button 
          className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('all');
            setPage(0);
            setLeaderboardData([]);
          }}
        >
          All Students
        </button>
        <button 
          className={`tab-btn ${activeTab === 'department' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('department');
            setPage(0);
            setLeaderboardData([]);
          }}
        >
          Your Department
        </button>
        <button 
          className={`tab-btn ${activeTab === 'departments' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('departments');
          }}
        >
          Department Rankings
        </button>
      </div>

      <div className="leaderboard-content">
        {loading ? (
          <div className="loading-state">
            <div className="top-three">
              {[1, 2, 3].map((index) => (
                <div key={index} className={`top-card rank-${index}`} style={{ padding: '20px' }}>
                  <SkeletonLoader type="avatar" width="60px" height="60px" />
                  <SkeletonLoader type="title" width="120px" />
                  <SkeletonLoader type="text" width="80px" />
                  <SkeletonLoader type="text" width="100px" />
                  <SkeletonLoader type="text" width="60px" />
                </div>
              ))}
            </div>
            <div className="leaderboard-list">
              {[4, 5, 6, 7, 8].map((index) => (
                <div key={index} className="leaderboard-item">
                  <SkeletonLoader type="text" width="40px" />
                  <SkeletonLoader type="avatar" width="40px" height="40px" />
                  <div className="info" style={{ flex: 1 }}>
                    <SkeletonLoader type="title" width="150px" />
                    <SkeletonLoader type="text" width="200px" />
                  </div>
                  <SkeletonLoader type="text" width="80px" />
                </div>
              ))}
            </div>
          </div>
        ) : activeTab === 'departments' ? (
          /* Department Rankings View */
          <div className="department-rankings">
            {departmentStats.map((dept, index) => (
              <div key={dept.department} className={`department-card ${index < 3 ? `rank-${index + 1}` : ''}`}>
                <div className="dept-rank">#{dept.rank}</div>
                <div className="dept-info">
                  <h3>{dept.department}</h3>
                  <div className="dept-stats">
                    <span>{dept.memberCount} members</span>
                    <span>•</span>
                    <span>{dept.averagePoints} avg pts</span>
                  </div>
                </div>
                <div className="dept-points">
                  <div className="total-points">{dept.totalPoints.toLocaleString()}</div>
                  <div className="points-label">total points</div>
                </div>
                {index === 0 && <div className="crown">🏆</div>}
              </div>
            ))}
          </div>
        ) : (
          /* Individual Leaderboard View */
          <>
            <div className="top-three">
              {leaderboardData.slice(0, 3).map((student, index) => (
            <div key={index} className={`top-card rank-${index + 1}`}>
              <div className="crown">
                {index === 0 ? '🐐' : index === 1 ? '🥈' : '🥉'}
              </div>
              <div className="badge">{getRankBadge(student.points)}</div>
              <h3>{student.name}</h3>
              <p className="roll">{student.rollNo}</p>
              <p className="department">{student.department}</p>
              <p className="points">{student.points} pts</p>
            </div>
          ))}
        </div>

        <div className="leaderboard-list">
          {leaderboardData.slice(3).map((student) => (
            <div key={student.rank} className="leaderboard-item">
              <div className="rank">#{student.rank}</div>
              <div className="badge">{getRankBadge(student.points)}</div>
              <div className="info">
                <h4>{student.name}</h4>
                <p>{student.rollNo} • {student.department}</p>
              </div>
              <div className="points">
                <span className="points-value">{student.points}</span>
                <span className="points-label">points</span>
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {hasMore && !loading && (
            <div className="load-more-container">
              <button 
                onClick={loadMore} 
                className="load-more-btn"
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : `Load More (Showing ${leaderboardData.length} of ${totalUsers})`}
              </button>
            </div>
          )}
          
          {/* Current User Position - always show at bottom if not already visible */}
          {currentUserData && !leaderboardData.some(entry => entry.id === user?.id) && (
            <>
              <div className="user-position-separator">
                <div className="separator-line"></div>
                <span>Your Position</span>
                <div className="separator-line"></div>
              </div>
              
              <div className="leaderboard-item current-user">
                <div className="rank">#{currentUserData.rank}</div>
                <div className="badge">{getRankBadge(currentUserData.points)}</div>
                <div className="info">
                  <h4>You ({currentUserData.name})</h4>
                  <p>{currentUserData.rollNo} • {currentUserData.department}</p>
                </div>
                <div className="points">
                  <span className="points-value">{currentUserData.points}</span>
                  <span className="points-label">points</span>
                </div>
              </div>
            </>
          )}
        </div>

        <div className="prize-card">
          <div className="prize-icon">🏆</div>
          <h3>Monthly Prize</h3>
          <p>Top 3 Monthly contributors win Rs. 1000 cash prizes + certificates!</p>
          <div className="countdown">Contest ends in 12 days</div>
        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;